import { serverRootUrl } from '@/api/client';
import { toEchoSlug } from './echoUtils';
import whatsappIcon from '@/assets/images/svg/whatsapp_icon.svg';
import facebookIcon from '@/assets/images/svg/facebook.svg';
import twitterIcon from '@/assets/images/svg/twitter.svg';
import instagramIcon from '@/assets/images/svg/instagram.svg';

/** Detect mobile/tablet devices via user agent. */
export function isMobileDevice(): boolean {
  return /Android|iPhone|iPad|iPod|webOS|BlackBerry|Opera Mini|IEMobile/i.test(navigator.userAgent);
}

/**
 * Try native OS share sheet.
 * Packs everything into the text field (text + URL) so all apps receive it.
 * WhatsApp/Facebook ignore the separate `url` field from browser shares.
 * Returns true if successful, false if unavailable or cancelled.
 */
export async function tryNativeShare(text: string, url: string, title?: string): Promise<boolean> {
  if (!navigator.share) return false;
  try {
    await navigator.share({ title: title ?? 'D.ECHO', text, url });
    return true;
  } catch {
    return false;
  }
}

/** Social platforms available in share modals, with display names and icon paths. */
export const SHARE_SOCIALS: Array<{ id: string; name: string; icon: string }> = [
  { id: 'whatsapp', name: 'WhatsApp', icon: whatsappIcon },
  { id: 'instagram', name: 'Instagram', icon: instagramIcon },
  { id: 'facebook', name: 'Facebook', icon: facebookIcon },
  { id: 'twitter', name: 'X(Twitter)', icon: twitterIcon },
];

/**
 * Open a social media share URL for the given platform.
 * On mobile/tablet, triggers the native OS share sheet (text + URL included).
 * On desktop, opens platform-specific share URLs.
 * Returns true if the platform was handled, false otherwise.
 */
export async function openSocialShareUrl(
  platform: string,
  encodedText: string,
  shareLink: string,
  rawText?: string
): Promise<boolean> {
  if (isMobileDevice() && rawText) {
    const shared = await tryNativeShare(rawText, shareLink);
    if (shared) return true;
  }

  switch (platform) {
    case 'whatsapp': {
      const url = isMobileDevice()
        ? `https://wa.me/?text=${encodedText}`
        : `https://web.whatsapp.com/send?text=${encodedText}`;
      window.open(url, '_blank', 'noopener');
      return true;
    }
    case 'facebook':
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink)}`,
        '_blank',
        'noopener'
      );
      return true;
    case 'twitter':
      window.open(
        `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodeURIComponent(shareLink)}`,
        '_blank',
        'noopener'
      );
      return true;
    default:
      return false;
  }
}

/**
 * Build the canonical share link for an echo profile.
 */
export function buildEchoShareLink(echoName: string): string {
  const slug = toEchoSlug(echoName);
  const appUrl = import.meta.env['VITE_APP_URL'] || 'https://app.dechoes.com';
  return slug ? `${appUrl}/messages/${slug}` : 'https://dechoes.com';
}

/**
 * Build an OG-tagged share link for an echo profile.
 * Points to the backend /share/ endpoint which serves dynamic OG meta tags.
 * Social crawlers (Facebook, Twitter) see the tags; normal users get redirected to the SPA.
 */
export function buildEchoOgShareLink(echoName: string): string {
  const slug = toEchoSlug(echoName);
  if (!slug) return 'https://dechoes.com';
  return `${serverRootUrl}/api/share/${slug}`;
}

/**
 * Build a share link with OG tags for social crawlers.
 * Points to the backend /share/ endpoint which serves dynamic OG meta tags.
 * Normal users get redirected to the SPA automatically.
 */
export function buildMessageShareLink(echoName: string, messageText?: string): string {
  const slug = toEchoSlug(echoName);
  if (!slug) return 'https://dechoes.com';
  const base = `${serverRootUrl}/api/share/${slug}/message`;
  if (messageText) {
    return `${base}?text=${encodeURIComponent(messageText.slice(0, 200))}`;
  }
  return base;
}

/**
 * Try native share with an image file attached.
 * Uses navigator.share({ files }) on mobile browsers that support it.
 * Falls back to text-only native share, then returns false if unavailable.
 */
export async function tryNativeShareWithImage(
  text: string,
  url: string,
  imageBlob: Blob | null,
  title?: string
): Promise<boolean> {
  if (!navigator.share) return false;

  // Try sharing with image file if blob exists and canShare supports files
  if (imageBlob && navigator.canShare) {
    const file = new File([imageBlob], 'decho-share.png', { type: 'image/png' });
    const shareData = { title: title ?? 'D.ECHO', text, url, files: [file] };
    if (navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
        return true;
      } catch {
        return false;
      }
    }
  }

  // Fallback to text-only native share
  return tryNativeShare(text, url, title);
}

/** Trigger a browser download for a Blob. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
